import { OrdersApi, OrderConfirmationRequest, OrderResponse } from '@oncampusabroad/api';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { initialApiState, ApiCallBase, createApiConfiguration } from './apiConfiguration';

interface OrderConfirmationCall extends ApiCallBase {
  response: any; 
}

interface OrderCall extends ApiCallBase {
  response: OrderResponse;
}

interface State {
  orderConfirmation: OrderConfirmationCall;
  order: OrderCall;
}

const initialState: State = {
  orderConfirmation: initialApiState,
  order: initialApiState,
};

export const confirmOrder = createAsyncThunk(
  'orders/confirmOrder',
  async (request: OrderConfirmationRequest, { rejectWithValue }) => {
    try {
      const ordersApi = new OrdersApi(createApiConfiguration());
      const response = await ordersApi.apiV2OrdersConfirmationPost(request);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        i18nMessage: error.response?.data?.i18nMessage ?? 'api.error.server_error',
        status: error.response?.status,
      });
    }
  }
);

export const getOrder = createAsyncThunk(
  'orders/getOrder',
  async ({ orderId, token }: { orderId: string; token?: string }, { rejectWithValue }) => {
    try {     
      if (!token) {
        return rejectWithValue({ i18nMessage: 'api.error.login_required' });
      }
      const config = createApiConfiguration(token);
      const ordersApi = new OrdersApi(config);
      const response = await ordersApi.getOrder(orderId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue({
        i18nMessage: error.response?.data?.i18nMessage ?? 'api.error.server_error',
        status: error.response?.status,
      });
    }
  }
);

const OrdersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    resetOrderConfirmation: (state) => {
      state.orderConfirmation = {
        ...initialApiState,
        response: state.orderConfirmation.response,
      };
    },
    resetOrderConfirmationResponse: (state) => {
      state.orderConfirmation = {
        ...initialApiState,
      };
    },
    resetOrder: (state) => {
      state.order = {
        ...initialApiState,
        response: state.order.response,
      };
    },
    resetOrderResponse: (state) => {
      state.order = {
        ...initialApiState,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(confirmOrder.pending, (state) => {
      state.orderConfirmation.loading = 'pending';
    });
    builder.addCase(confirmOrder.fulfilled, (state, action) => {
      state.orderConfirmation.loading = 'succeeded';
      state.orderConfirmation.response = action.payload;
    });
    builder.addCase(confirmOrder.rejected, (state, action) => {
      state.orderConfirmation.loading = 'failed';
      state.orderConfirmation.error = action.payload;
    });

    builder.addCase(getOrder.pending, (state) => {
      state.order.loading = 'pending';
    });
    builder.addCase(getOrder.fulfilled, (state, action) => {
      state.order.loading = 'succeeded';
      state.order.response = action.payload;
    });
    builder.addCase(getOrder.rejected, (state, action) => {
      state.order.loading = 'failed';
      state.order.error = action.payload;
    });
  },
});

export const {
  resetOrderConfirmation,
  resetOrderConfirmationResponse,
  resetOrder,
  resetOrderResponse,
} = OrdersSlice.actions;

export default OrdersSlice.reducer; 